import { useState, useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { useNotification } from "@/context/NotificationContext";
import { eraseReports } from "@/api/report.api";
import { extractErrorMessage } from "@/api/axios";
import { RECAPTCHA_TEST_KEY } from "@/constants";

interface EraseDataDialogProps {
  open: boolean;
  onClose: () => void;
}

export function EraseDataDialog({ open, onClose }: EraseDataDialogProps) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleClose = useCallback(() => {
    setEmail("");
    recaptchaRef.current?.reset();
    onClose();
  }, [onClose]);

  const handleSubmit = async () => {
    const token = recaptchaRef.current?.getValue();
    if (!email.trim()) {
      notify(t("erase.emailRequired"), "warning");
      return;
    }
    if (!token) {
      notify(t("messages.recaptchaError"), "warning");
      return;
    }

    setSubmitting(true);
    try {
      const result = await eraseReports(email.trim(), token);
      notify(t("erase.success", { count: result.deleted }), "success");
      handleClose();
    } catch (error) {
      notify(extractErrorMessage(error), "error");
    } finally {
      setSubmitting(false);
      recaptchaRef.current?.reset();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("erase.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label={t("form.email")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            autoFocus
          />
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.RECAPTCHA_SITE_KEY || RECAPTCHA_TEST_KEY}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("form.cancel")}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {t("erase.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
